// 📄 components/Configuracion/ConfiguracionPlantillasXml.tsx
// Gestión de las plantillas de referencia del "Validador de Estructura"
// propias de ESTE proyecto (antes se cargaban desde el propio Validador,
// ver ValidadorEstructura.tsx — ahora la gestión vive acá, el Validador
// solo consume). Las plantillas globales (fallback cuando el proyecto no
// tiene una propia) se gestionan aparte, desde /admin.
import { useEffect, useState } from "react";
import { FileCode, Plus, RefreshCw, Pencil, Trash2, Info } from "lucide-react";
import {
  listarPlantillasProyecto, eliminarPlantillaProyecto, PlantillaProyecto
} from "../../services/plantillaReferenciaService.ts";
import ConfirmDialog from "../ui/ConfirmDialog.tsx";
import CargarPlantillaModal from "./CargarPlantillaModal.tsx";
import EditarPlantillaProyectoModal from "./EditarPlantillaProyectoModal.tsx";

interface ConfiguracionPlantillasXmlProps {
  projectId: string;
  projectNombre?: string;
  // Todavía sin conectar a ningún rol — hoy cualquier miembro real del
  // proyecto que llega a esta pestaña puede cargar/renombrar/eliminar (el
  // propio route "config" en HomeCA.tsx ya excluye al rol "cliente"). Esta
  // prop deja el componente listo para una restricción más fina más
  // adelante, sin implementarla todavía.
  canEdit?: boolean;
}

const formatearFecha = (fecha: any): string => {
  if (!fecha) return "";
  const ms = typeof fecha?.toMillis === "function" ? fecha.toMillis() : (typeof fecha?._seconds === "number" ? fecha._seconds * 1000 : null);
  if (!ms) return "";
  return new Date(ms).toLocaleDateString();
};

export default function ConfiguracionPlantillasXml({ projectId, projectNombre, canEdit = true }: ConfiguracionPlantillasXmlProps) {
  const [plantillas, setPlantillas] = useState<PlantillaProyecto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [aEliminar, setAEliminar] = useState<PlantillaProyecto | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const [editando, setEditando] = useState<PlantillaProyecto | null>(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const data = await listarPlantillasProyecto(projectId);
      setPlantillas(data.sort((a, b) => a.nombre.localeCompare(b.nombre)));
    } catch (err) {
      console.error("Error cargando las plantillas del proyecto:", err);
      setError("No se pudieron cargar las plantillas de este proyecto.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleGuardado = () => {
    setModalOpen(false);
    cargar();
  };

  const handleEliminar = async () => {
    if (!aEliminar) return;
    setEliminando(true);
    try {
      await eliminarPlantillaProyecto(projectId, aEliminar.tipoDocumento);
      setAEliminar(null);
      cargar();
    } catch (err) {
      console.error("Error al eliminar la plantilla:", err);
    } finally {
      setEliminando(false);
    }
  };

  const handleGuardadoEdicion = () => {
    setEditando(null);
    cargar();
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <p className="text-sm">Cargando plantillas...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}

      <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Plantillas de referencia propias de este proyecto para el Validador de Estructura — tienen prioridad sobre la plantilla global del mismo tipo, sin afectar a otros proyectos.</span>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-300">Plantillas de este proyecto</h3>
        {canEdit && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Cargar plantilla
          </button>
        )}
      </div>

      {plantillas.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <FileCode className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Este proyecto todavía no tiene plantillas propias.</p>
          <p className="text-xs text-gray-600 mt-1">Se usarán las plantillas globales como referencia mientras tanto.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {plantillas.map((p) => (
            <div key={p.tipoDocumento} className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-indigo-500/10">
                <FileCode className="w-5 h-5 text-indigo-400" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-white truncate block">{p.nombre}</span>
                <p className="text-[11px] text-gray-500 mt-1 truncate">
                  {p.nombreArchivo || "Sin archivo"}
                  {formatearFecha(p.fecha) && <> · Cargada el {formatearFecha(p.fecha)}</>}
                </p>
              </div>
              {canEdit && (
                <>
                  <button
                    onClick={() => setEditando(p)}
                    className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
                    title="Editar"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setAEliminar(p)}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <CargarPlantillaModal
          projectId={projectId}
          projectNombre={projectNombre}
          onClose={() => setModalOpen(false)}
          onGuardado={handleGuardado}
        />
      )}

      {editando && (
        <EditarPlantillaProyectoModal
          projectId={projectId}
          plantilla={editando}
          onClose={() => setEditando(null)}
          onGuardado={handleGuardadoEdicion}
        />
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar esta plantilla?"
          message={`"${aEliminar.nombre}" se eliminará de este proyecto. A partir de ahí, el Validador usará la plantilla global de este tipo (si existe). Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? "Eliminando..." : "Eliminar"}
          danger
          onConfirm={handleEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
